import { Component, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PhonePipe } from '@jfw/angular/shared/pipes';
import {
  AccountService,
  isAccountContextSuite,
} from '@jfw/angular/shared/services';
import {
  ecomStatusLabel,
  eliteStatusLabel,
  establishedDateLabel,
} from '@jfw/types';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../services/auth/auth.service';

// EXT-6537: DRRNBR holds the rep's KMS id, or 'NONE' when there is no rep.
const NO_REP = new Set(['', 'NONE']);

@Component({
  selector: 'app-account-banner',
  templateUrl: './account-banner.component.html',
  styleUrls: ['./account-banner.component.scss'],
  // EXT-6537: the bar is now its own pinned row above the module tabs, so the
  // sticky `.account-topper` class (the module tabs' stack selector) lives
  // here, and only while there is a bar to show. EXT-6573: the bar shows with
  // no account too, gray, holding just the account lookup.
  host: {
    '[class.account-topper]': 'displayBar()',
    '[class.no-account]': 'displayBar() && !platformAccount()',
  },
  imports: [MatIconModule, PhonePipe],
})
export class AccountBannerComponent {
  private accountService: AccountService = inject(AccountService);
  private authService = inject(AuthService);

  platformAccount = this.accountService._platformAccount;
  private currentSuite = toSignal(this.authService.currentSuite$);

  displayBar = computed(() => isAccountContextSuite(this.currentSuite()?.enum));

  // EXT-6207: the account details are highlighted while the drawer is open on
  // its Account tab. Both signals matter -- `drawerState` keeps its last value
  // after the drawer is closed, so without `showDrawer` a closed drawer would
  // still mark it.
  drawerOpen = this.accountService.showDrawer;
  private drawerState = this.accountService.drawerState;
  activeTab = computed(() =>
    this.drawerOpen() ? this.drawerState() : undefined,
  );

  // EXT-6535: the account's own phone (ACCTMAST.PHON) ends the address line.
  // PHON can come back blank or space-padded, so blank means no phone at all.
  phone = computed(() => this.platformAccount()?.phone?.trim() || undefined);

  addressLine = computed(() => {
    const { streetAddress1, streetAddress2, city, state, zip } =
      this.platformAccount()?.address ?? {};
    const street = [streetAddress1, streetAddress2].filter(Boolean).join(', ');
    const cityState = [city, state].filter(Boolean).join(', ');
    return [street, [cityState, zip].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
  });

  mdm = computed(() => {
    const rep = this.platformAccount()?.dealerRelationsRep?.trim() ?? '';
    return NO_REP.has(rep.toUpperCase()) ? '—' : rep;
  });

  established = computed(() =>
    establishedDateLabel(this.platformAccount()?.establishedOn),
  );

  eliteLabel = computed(() =>
    eliteStatusLabel(this.platformAccount()?.eliteStatus),
  );
  ecomLabel = computed(() =>
    ecomStatusLabel(this.platformAccount()?.ecomStatus),
  );

  // EXT-6573: the › toggle reopens the drawer on whichever tab it last showed.
  toggleDrawer(): void {
    this.drawerOpen.update((open) => !open);
  }

  showDrawer(drawerState: string) {
    this.accountService.selectDrawerTab(drawerState);
  }
}
